import { c as _c } from "react/compiler-runtime";
import { getStoredAuthToken, removeStoredAuthToken, storeAuthToken } from 'src/auth/authToken';
import { filter } from 'ramda';
import { shutdown as shutDownIntercom } from 'shared/intercom';
import type { ReactNode } from 'react';
import React, { createContext, useContext, useEffect, useState } from 'react';
import type * as auth from 'shared/api/auth';
import type * as fetch from 'shared/api/fetch';
import { get, removeAll as removeAllFromCookieStore, store } from 'shared/cookieStore';
import { noop, noopAsync, redirectToPage } from 'shared/utils';
import { UseQueryLoggedInUser, useSignIn, useSignUp } from 'src/auth/api';
import { APP_ROUTES } from 'shared/routes';
import { clearPendingInviteReturnTo } from 'src/auth/pendingInviteReturnTo';
type mode = 'sign-in' | 'sign-up';
export type AuthError = fetch.ClientError | null;
export interface User {
  email: string;
  displayName?: string;
  name: string;
  mode?: mode;
  idTokenProvider?: auth.tokenProviders;
  idToken?: string;
  id?: number;
  hasInternalFeatureAccess?: boolean;
}
interface IAuthContext {
  authenticated: boolean;
  currentUser?: User;
  updateUserName: (name: string) => void;
  login: (user: auth.AuthSignInReq) => Promise<void>;
  signup: (user: auth.AuthSignUpReq) => Promise<void>;
  logOut: (redirectUrl?: string) => void;
  mustAuth: boolean;

  // when we just started
  // login attempt or a we are currently checking logged in user

  isLoading: boolean;

  // a user successfully logs in or on reload we are able to retrieve the current logged in useer
  isSuccess: boolean;
  authToken: string;
  error: AuthError;
  signInError: AuthError;
  signUpError: AuthError;
  alreadyLoggedInError: AuthError;
  providerError: AuthError;
  resetSignInError: () => void;
  resetSignUpError: () => void;
  setProviderError: (error: AuthError) => void;
}
const defaultValue: IAuthContext = {
  authenticated: false,
  currentUser: {
    email: '',
    name: '',
    displayName: '',
    idToken: '',
    hasInternalFeatureAccess: false
  },
  updateUserName: noop,
  login: noopAsync,
  logOut: noop,
  signup: noopAsync,
  mustAuth: false,
  isLoading: true,
  isSuccess: false,
  authToken: '',
  signInError: null,
  signUpError: null,
  alreadyLoggedInError: null,
  providerError: null,
  error: null,
  resetSignInError: noop,
  resetSignUpError: noop,
  setProviderError: noop
};
const first = (list: AuthError[]): AuthError => list[0];
const getCurrentAuthError = filter((error: AuthError) => error?.status_code != null);
interface Props {
  children: ReactNode;
}
const AuthContext = createContext<IAuthContext>(defaultValue);
export const useAuth = () => {
  return useContext(AuthContext);
};
interface ISaveCurrentUser {
  userName: string;
  userEmail: string;
  userId?: number;
  expires?: string;
}
export const saveCurrentUser = ({
  userName,
  userEmail,
  userId,
  expires
}: ISaveCurrentUser): void => {
  store('userName', userName, expires);
  store('userEmail', userEmail, expires);
  if (userId != null) store('userId', userId.toString(), expires);
};
const getCurrentUserFromCookieStore = (): User => {
  const rawUserId = get('userId');
  const parsedUserId = rawUserId != null && rawUserId !== '' ? Number(rawUserId) : undefined;
  const id = parsedUserId != null && Number.isFinite(parsedUserId) ? parsedUserId : undefined;
  return {
    name: get('userName') ?? '',
    email: get('userEmail') ?? '',
    ...(id != null && {
      id
    })
  };
};
const clearStore = (): void => {
  removeAllFromCookieStore();
};
export const AuthProvider = props => {
  const $ = _c(30);
  const [authenticated, setAuthenticated] = useState(defaultValue.authenticated);
  const [currentUser, setCurrentUser] = useState(defaultValue.currentUser as User);
  const [authToken, setAuthToken] = useState("");
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      setCurrentUser({
        ...defaultValue.currentUser,
        ...getCurrentUserFromCookieStore()
      });
      setAuthToken(getStoredAuthToken() ?? "");
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  const {
    mutateAsync: createNewLogin,
    isSuccess: isSignInSuccessful,
    isPending: isSigningIn,
    error: signInError,
    reset: resetSignInError
  } = useSignIn();
  const {
    mutateAsync: createNewUser,
    isSuccess: isSignUpSuccessful,
    isPending: isSigningUp,
    error: signUpError,
    reset: resetSignUpError
  } = useSignUp();
  const [providerError, setProviderError] = useState(null);
  const {
    data: alreadyLoggedInUser,
    isSuccess: isAlreadyLoggedUser,
    isLoading: isCheckingAlreadyLoggedUser,
    error: alreadyLoggedInError
  } = UseQueryLoggedInUser(authToken);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      removeStoredAuthToken();
      setAuthToken("");
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const doAwayWithToken = t2;
  let t3;
  if ($[3] !== createNewLogin) {
    t3 = async user => {
      doAwayWithToken();
      await createNewLogin(user, {
        onSuccess: newUser => {
          setAuthenticated(true);
          setCurrentUser(newUser);
          storeAuthToken(newUser.accessToken, newUser.expires);
          setAuthToken(newUser.accessToken);
          saveCurrentUser({
            userName: newUser.name,
            userEmail: newUser.email,
            expires: newUser.expires
          });
        }
      });
    };
    $[3] = createNewLogin;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const login = t3;
  const [isCreatingNewUser, setIsCreatingNewUser] = useState(false);
  let t4;
  if ($[5] !== createNewUser) {
    t4 = async user_0 => {
      doAwayWithToken();
      setIsCreatingNewUser(true);
      await createNewUser(user_0, {
        onSuccess: newUser_0 => {
          setAuthenticated(true);
          setCurrentUser(newUser_0);
          storeAuthToken(newUser_0.accessToken, newUser_0.expires);
          setAuthToken(newUser_0.accessToken);
          saveCurrentUser({
            userName: newUser_0.name,
            userEmail: newUser_0.email,
            expires: newUser_0.expires
          });
        },
        onSettled: () => {
          setIsCreatingNewUser(false);
        }
      });
    };
    $[5] = createNewUser;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const signup = t4;
  useEffect(() => {
    if (isAlreadyLoggedUser) {
      setAuthenticated(true);
      setCurrentUser(Object.assign({
        id: alreadyLoggedInUser.userId,
        email: alreadyLoggedInUser.userEmail,
        hasInternalFeatureAccess: alreadyLoggedInUser.hasInternalFeatureAccess
      }, getCurrentUserFromCookieStore()));
      if (alreadyLoggedInUser.userId != null) {
        store("userId", alreadyLoggedInUser.userId.toString());
      }
    }
  }, [alreadyLoggedInUser, isAlreadyLoggedUser]);
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = t6 => {
      const redirectUrl = t6 === undefined ? APP_ROUTES.auth.signInPage : t6;
      clearPendingInviteReturnTo();
      clearStore();
      setAuthenticated(false);
      setCurrentUser(defaultValue.currentUser as User);
      shutDownIntercom();
      redirectToPage(redirectUrl);
    };
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const logOut = t5;
  const updateUserName = name => {
    setCurrentUser(Object.assign(getCurrentUserFromCookieStore(), {
      name,
      id: alreadyLoggedInUser?.userId,
      email: alreadyLoggedInUser?.userEmail,
      hasInternalFeatureAccess: alreadyLoggedInUser?.hasInternalFeatureAccess
    }));
  };
  const isSuccess = isAlreadyLoggedUser || isSignInSuccessful || isSignUpSuccessful;
  const isLoading = isCheckingAlreadyLoggedUser || isSigningIn || isSigningUp || isCreatingNewUser;
  const t6 = AuthContext;
  const t7 = currentUser?.email === "" ? undefined : currentUser;
  const t8 = !isSuccess;
  const t9 = first(getCurrentAuthError([alreadyLoggedInError, signInError, signUpError, providerError]));
  let t10;
  if ($[8] !== alreadyLoggedInError || $[9] !== authToken || $[10] !== authenticated || $[11] !== isLoading || $[12] !== isSuccess || $[13] !== logOut || $[14] !== login || $[15] !== providerError || $[16] !== resetSignInError || $[17] !== resetSignUpError || $[18] !== signInError || $[19] !== signUpError || $[20] !== signup || $[21] !== t7 || $[22] !== t8 || $[23] !== t9 || $[24] !== updateUserName) {
    t10 = {
      authenticated,
      currentUser: t7,
      updateUserName,
      login,
      signup,
      logOut,
      authToken,
      mustAuth: t8,
      isLoading,
      error: t9,
      signInError,
      signUpError,
      alreadyLoggedInError,
      providerError,
      resetSignInError,
      resetSignUpError,
      setProviderError,
      isSuccess
    };
    $[8] = alreadyLoggedInError;
    $[9] = authToken;
    $[10] = authenticated;
    $[11] = isLoading;
    $[12] = isSuccess;
    $[13] = logOut;
    $[14] = login;
    $[15] = providerError;
    $[16] = resetSignInError;
    $[17] = resetSignUpError;
    $[18] = signInError;
    $[19] = signUpError;
    $[20] = signup;
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
    $[24] = updateUserName;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== props.children || $[27] !== t10 || $[28] !== t6.Provider) {
    t11 = <t6.Provider value={t10}>{props.children}</t6.Provider>;
    $[26] = props.children;
    $[27] = t10;
    $[28] = t6.Provider;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  return t11;
};